import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { ApplicationEntryLayout } from '../components/ApplicationEntryLayout/ApplicationEntryLayout';

import ValidationGroup from 'devextreme-react/validation-group';
import { useValidateAndSaveApplication } from '../hooks/useValidateAndSaveApplication';
import '../scss/_applicationEntryLayout.scss';
import { BorrowerReviewPanel } from './components/BorrowerReviewPanel';
import { PropertyReviewPanel } from './components/PropertyReviewPanel';
import { AddAnotherPropertyPanel } from './components/AddAnotherPropertyPanel';
import { ReviewDisclaimer } from './components/ReviewDisclaimer';
import {
    constructAgreementHtml,
    generalAcknowledgementTemplate,
    rentalAcknowledgementTemplate,
} from './components/ReviewSubmission.tsx';
import '../scss/_reviewPage.scss';
import { ShowIf } from '../components/ShowIf';
import { LoanType } from '@apply/constants';
import WebFont from 'webfontloader';
import { fonts } from '@apply/ui';
import { useSaveApplicationWithoutValidation } from '../hooks/useSaveApplicationWithoutValidation';
import { useRedirectIfComplete } from '../hooks/useRedirectIfApplicationComplete';
import { CurrentApplicationPage, useNavigateApplication } from '../hooks/useNavigateApplication';
import { usePausePointTracker } from '../hooks/usePausePointTracker.tsx';
import { useWaitOverlayStore } from '@apply/components';
import { useApplicationQuery } from '../../queries/applicationQueries';
import { useBorrowerLoanType } from '../../queries/BorrowerFields/CoreFields';
import { useBorrowerEsignAcknowledgeFontName, useApplicationProperties } from '../../queries/BorrowerFields/ApplicationFields';
import {
    useBorrowerEsignTypedName,
    useBorrowerEsignTitle,
    useBorrowerEsignDate,
    useBorrowerEsignIpAddress,
} from '../../queries/BorrowerFields/EsignFields';
import { ApplicationCrumbType } from '../components/ApplicationEntryLayout/components/ApplicationEntrySections/types/ApplicationCrumbType.tsx';

export interface ReviewPageProps {
    printMode?: boolean;
}

export const Review: React.FC<ReviewPageProps> = ({ printMode = false }) => {
    const { entryGuid } = useParams<{ entryGuid?: string }>();
    const { search } = useLocation();
    const params = new URLSearchParams(search);
    const token = params.get('token');
    const { isPending, data: applicationData } = useApplicationQuery(entryGuid, token);
    const navigate = useNavigate();
    const validationGroupRef = useRef(null);
    const { validateAndSaveApplication } = useValidateAndSaveApplication(validationGroupRef);
    const { saveApplicationNoValidation } = useSaveApplicationWithoutValidation();

    const [properties] = useApplicationProperties();
    const propertyGuids = properties?.items?.map((p) => p.guid) ?? [];
    const lastPropertyGuid = propertyGuids[propertyGuids.length - 1];

    const { navigateApplicationForward, navigateApplicationBackward } = useNavigateApplication({
        page: CurrentApplicationPage.Review,
        entryGuid,
        propertyGuid: lastPropertyGuid,
    });
    const { updatePausePoint, clearPausePoint } = usePausePointTracker();

    const [loanType] = useBorrowerLoanType();
    const [typedName] = useBorrowerEsignTypedName();
    const [title] = useBorrowerEsignTitle();
    const [date] = useBorrowerEsignDate();
    const [acknowledgeFontName] = useBorrowerEsignAcknowledgeFontName();
    const [ipAddress] = useBorrowerEsignIpAddress();

    useRedirectIfComplete(printMode);

    useEffect(() => {
        if (!isPending) {
            useWaitOverlayStore.getState().setWaiting(false);
        }
    }, [isPending]);

    useEffect(() => {
        (async () => {
            const fontNames = fonts.map((font) => font.name);

            WebFont.load({
                google: {
                    families: fontNames,
                },
            });
        })();
    }, []);

    useEffect(() => {
        if (printMode) document.body.style.backgroundColor = '#fff';
    }, [printMode]);

    if (isPending && entryGuid) {
        return null;
    }

    const onFinalizeApplication = async (e: React.MouseEvent<Element, MouseEvent>) => {
        if (e) e.preventDefault();

        clearPausePoint();

        const applicationResponse = await validateAndSaveApplication();

        if (!applicationResponse) return;

        navigateApplicationForward();
    };

    const onSaveAndExit = async (e: React.MouseEvent<Element, MouseEvent>) => {
        if (e) e.preventDefault();

        updatePausePoint();

        const applicationResponse = await saveApplicationNoValidation();

        if (!applicationResponse) return;

        navigate(`/home`);
    };

    const nextStepButton = {
        id: 'finalizeApplicationButton',
        dataTestid: 'finalizeApplicationButton',
        children: 'Finalize',
        handleClick: onFinalizeApplication,
        disabled: false,
    };

    const saveAndExitButton = {
        id: 'saveAndExitApplicationButton',
        dataTestid: 'saveAndExitApplicationButton',
        children: 'Save and Exit',
        handleClick: onSaveAndExit,
        disabled: false,
    };

    const previousStepButtonClick = async (e: React.MouseEvent<Element, MouseEvent>) => {
        if (e) e.preventDefault();

        clearPausePoint();

        const applicationResponse = await saveApplicationNoValidation();

        if (!applicationResponse) return;

        navigateApplicationBackward();
    };

    return (
        <div className={printMode ? 'print' : ''}>
            <ValidationGroup ref={validationGroupRef}>
                <ApplicationEntryLayout
                    currentCrumb={ApplicationCrumbType.Review}
                    saveAndExitButtonProps={saveAndExitButton}
                    nextStepButtonProps={nextStepButton}
                    previousStepButtonVisible={true}
                    previousStepButtonClick={previousStepButtonClick}
                    overrideDefaultContentWidth={true}
                    printMode={printMode}
                >
                    <ShowIf
                        showIf={printMode || (loanType !== LoanType.SubNoteConstruction && loanType !== LoanType.SubNoteFixFlip)}
                    >
                        <BorrowerReviewPanel
                            printMode={printMode}
                            entryGuid={entryGuid}
                            token={token}
                        />
                    </ShowIf>
                    <br />
                    {propertyGuids?.map((propGuid, index) => (
                        <React.Fragment key={propGuid}>
                            <PropertyReviewPanel
                                printMode={printMode}
                                propertyGuid={propGuid}
                                propertyIndex={index + 1}
                                entryGuid={entryGuid}
                                token={token}
                                propertyCount={propertyGuids?.length}
                            />
                            <br />
                        </React.Fragment>
                    ))}
                    {
                        //TODO: HANDLE FORM COMPLETED IN THIS CHECK
                    }
                    <ShowIf showIf={!printMode}>
                        <>
                            <AddAnotherPropertyPanel
                                printMode={printMode}
                                entryGuid={entryGuid}
                            />
                            <ReviewDisclaimer />
                        </>
                    </ShowIf>
                    {
                        //TODO: This is v1 functionality and needs to be checked against printMode after sign off before go live
                        printMode && typedName && title && date && acknowledgeFontName && (
                            <div className='row'>
                                <div
                                    className='columns'
                                    style={{
                                        fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                                        paddingTop: '20px',
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: constructAgreementHtml(
                                            loanType == LoanType.Rental
                                                ? rentalAcknowledgementTemplate
                                                : generalAcknowledgementTemplate,
                                            typedName ?? '',
                                            title ?? '',
                                            new Date(date ?? ''),
                                            'Mr Dafoe', //TODO: Hardcoded for now, do we want to still support multiple fonts?
                                            ipAddress ?? ''
                                        ),
                                    }}
                                ></div>
                            </div>
                        )
                    }
                </ApplicationEntryLayout>
            </ValidationGroup>
        </div>
    );
};
